/**
 * THE GRAPH. Five nodes at rest, and nothing on the main thread between events.
 *
 * -- WHAT IS RUNNING WHILE THE GAME IDLES ----------------------------------------
 *
 *     swell buffer  --\
 *                      >-- sea -----\
 *     surface buffer --/             >-- master --> destination
 *     (a moment, briefly) -- effects /
 *
 * Two looping AudioBufferSourceNodes, three gains, and the destination. `sea` and `effects` are
 * the two buses the player's settings can level (Task 5); every one-shot - a landing, a record,
 * the paper, the mark - goes through `effects`, and nothing is ever connected to `master`
 * directly, so neither bus exists without a source behind it. A looping buffer source is
 * the cheapest continuous thing the API has - it copies samples, it does not compute them - and
 * there is no oscillator, no filter, no LFO and no ScriptProcessor anywhere in this lane. On the
 * MAIN thread there is nothing at all: no interval, no animation frame, no timer. The host page
 * already sleeps between draws and this lane does not wake it.
 *
 * That is not a preference in this run, it is a bar - it is why the game has no React - so it is
 * measured rather than asserted: `stats()` publishes the node count and the render cost, and the
 * report for this round carries the main-thread figure with audio on against audio off.
 *
 * -- THE FIRST GESTURE -----------------------------------------------------------
 *
 * Every browser refuses to start audio before the player has touched the page, and a game that
 * throws or logs when it finds that out is a broken game for anybody who never clicks. Nothing
 * here is constructed until a gesture arrives: no AudioContext, no buffers, no thread. `arm`
 * attaches one-shot listeners and returns; a player who reads the sea for an hour without
 * touching it costs this lane one dormant listener and zero bytes of audio.
 *
 * -- HIDDEN MEANS SILENT, AND THAT IS ALSO THE IDLE ANSWER -----------------------
 *
 * Web Audio is exempt from background-tab throttling, so a tab in another window goes on paying
 * for its audio thread forever. Suspending the context when the document is hidden takes that to
 * zero, and it is what a player wants anyway: sound from a tab you cannot see is a fault report,
 * not ambience. Suspend keeps the buffer position, so coming back resumes mid-wave rather than
 * restarting the loop, and the mute preference is untouched by any of it.
 */

import { CATCH_PHASE_END_MS } from '../app/catch-presentation';
import { eventsBetween, snapshotOf, type AudioEvent, type AudioSnapshot } from './events';
import { renderMomentBank, LAND_BUCKETS, LAND_VARIANTS, type MomentBank } from './moments';
import { hashText } from './rng';
import { levelSurfacePair, renderSurfaceSide, renderSwell, surfaceEnvelopes, SURFACE_CHANNELS, type SeaLayer } from './sea';
import type { Samples } from './samples';
import { FADE, LAND_MIN_GAP_MS, MUTE_KEY, SURFACE, TOLD_KEY } from './spec';

/** At most this many one-shots at once. A drip is a bug; a swarm is a worse one. */
const MAX_VOICES = 8;
/**
 * MOMENTS FOLLOW THE CEREMONY. `observe` hears a landing the step the save changes, which is
 * the instant Task 3's catch ceremony begins; the fish is seen coming aboard at the PULL and the
 * plate opens at the REVEAL, so the landing is scheduled for the one and the record chime for
 * the other, from the same phase table the water and the plate read. Seconds.
 */
const LAND_AT = CATCH_PHASE_END_MS.bite / 1000;
const RECORD_AT = CATCH_PHASE_END_MS['reel-window'] / 1000;

/** The three levels and the mute the player's settings hold, pushed in as one. */
export interface AudioMix {
  readonly master: number;
  readonly sea: number;
  readonly effects: number;
  readonly muted: boolean;
}

export interface AudioStats {
  /** False until the first gesture. Everything else is meaningless while it is. */
  readonly started: boolean;
  readonly muted: boolean;
  /** The master level applied when neither muted nor hidden. */
  readonly volume: number;
  /** The sea bus level. */
  readonly sea: number;
  /** The effects bus level. */
  readonly effects: number;
  readonly hidden: boolean;
  readonly contextState: string | null;
  readonly sampleRate: number | null;
  /** Nodes alive right now. Four is the resting graph; more means a moment is sounding. */
  readonly nodes: number;
  /** Wall-clock milliseconds the one-time render of every buffer took. MEASURED, at unlock. */
  readonly renderMs: number;
  /** The same, split by task, so the longest single block of the main thread is visible. */
  readonly renderStepMs: readonly number[];
  /** Float32 samples held in AudioBuffers. Times four is bytes. */
  readonly bufferSamples: number;
  /** Loop lengths in seconds, and therefore how long before the pair repeats. */
  readonly loopSeconds: readonly number[];
  readonly momentsPlayed: number;
  readonly landsSuppressed: number;
}

export interface AudioEngine {
  /**
   * Attach first-gesture listeners. Constructs nothing. Safe to call when audio is impossible.
   *
   * `onStarted` runs once, on the gesture that actually brings the context up, and is how the
   * host knows to say the one line that tells the player which key quiets it. It runs whether
   * or not the preference is muted, because a returning muted player still pressed a key and a
   * silent unlock is exactly the case a host would otherwise never learn about.
   */
  arm(onStarted?: () => void): void;
  /** True once the context exists and the sea is playing. */
  readonly started: boolean;
  readonly muted: boolean;
  /** Set the mute preference and persist it. Returns the new value. */
  setMuted(next: boolean): boolean;
  toggleMuted(): boolean;
  /**
   * THE ONE MIX API. Take the master, sea and effects levels (0 to 1) and the mute from the
   * player's profile, without building anything: this runs at page open, before any gesture,
   * where a constructed context would only be refused, and again on every settings change.
   * A context that exists ramps to the new levels; one that does not reads them when it comes
   * up. The levels are not persisted here - they live in `PlayerSettings`; the mute is also
   * written to the engine's own key so it stays the pre-profile fallback the next open reads.
   * `setMuted` remains for the gesture path (the S key), which may construct the context.
   */
  setMix(mix: AudioMix): void;
  readonly volume: number;
  /** True the first time it is called on this browser: the player has not been told the key yet. */
  claimFirstTelling(): boolean;
  /** Compare the save against the last look and sound whatever changed. */
  observe(snapshot: AudioSnapshot): void;
  /** Forget the last look, for a save that was reset or imported. Emits nothing. */
  forget(): void;
  /** The sheet moving. */
  sheet(up: boolean): void;
  /** The player using the one verb. */
  mark(): void;
  /** Fire one moment directly. For scripts and for the proof surface. */
  play(event: AudioEvent): void;
  stats(): AudioStats;
}

/** Read the persisted mute preference. A browser that refuses storage reads as unmuted. */
function readMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMuted(muted: boolean): void {
  try {
    window.localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* a private window; the choice lives for this tab only */
  }
}

/** Copy a rendered layer into an AudioBuffer of the context. */
function toAudioBuffer(ctx: BaseAudioContext, layer: SeaLayer): AudioBuffer {
  const buf = ctx.createBuffer(layer.channels.length, layer.length, layer.sampleRate);
  for (let c = 0; c < layer.channels.length; c++) buf.copyToChannel(layer.channels[c] as Samples, c);
  return buf;
}

function monoBuffer(ctx: BaseAudioContext, data: Float32Array): AudioBuffer {
  const buf = ctx.createBuffer(1, data.length, ctx.sampleRate);
  buf.copyToChannel(data as Samples, 0);
  return buf;
}

/** One piece of the render, small enough that owning the thread for it drops at most a frame. */
interface BakeStep {
  readonly name: string;
  run(): void;
}

/**
 * Resume or suspend, with the rejection swallowed.
 *
 * `AudioContext.resume()` REJECTS when the browser has not seen a user gesture, and a bare
 * `void promise` leaves that rejection unhandled, which Chrome reports on the console. This lane
 * has a standing requirement that a player who never clicks sees no error, so every transition
 * goes through here. There is nothing to do about a refusal except stay silent, which is the
 * correct behaviour anyway.
 */
function transition(ctx: AudioContext, to: 'running' | 'suspended'): void {
  const p = to === 'running' ? ctx.resume() : ctx.suspend();
  void p.catch(() => {
    /* the browser declined; the game is unchanged and nothing is written to the console */
  });
}

export function createAudioEngine(): AudioEngine {
  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;
  let seaGain: GainNode | null = null;
  let effectsGain: GainNode | null = null;
  /**
   * WHAT EACH LOOP IS, WITHOUT KEEPING A SECOND COPY OF IT.
   *
   * `copyToChannel` copies the samples into the AudioBuffer, so holding the source Float32Arrays
   * afterwards doubles the memory for nothing - about 7.5 MiB of it. Only the shape is kept, and
   * `stats()` reports off that.
   */
  const seaShapes: { length: number; channels: number; seconds: number }[] = [];
  let bank: MomentBank | null = null;
  let renderMs = 0;
  const stepMs: number[] = [];
  const loops: AudioBufferSourceNode[] = [];
  let momentBuffers: { land: AudioBuffer[][]; junk: AudioBuffer[]; record: AudioBuffer[]; lift: AudioBuffer | null; settle: AudioBuffer | null; mark: AudioBuffer[] } | null = null;

  let muted = readMuted();
  let volume = 1;
  let seaLevel = 1;
  let effectsLevel = 1;
  let hidden = false;
  let voices = 0;
  let momentsPlayed = 0;
  let landsSuppressed = 0;
  let lastLandAtMs = -1e12;
  let last: AudioSnapshot | null = null;
  let armed = false;
  let onStarted: (() => void) | null = null;

  const nowMs = (): number => (typeof performance === 'undefined' ? Date.now() : performance.now());
  const level = (next: number, fallback: number): number => (Number.isFinite(next) ? Math.min(1, Math.max(0, next)) : fallback);

  /** The gain the master should be at, given the two things that can silence it. */
  const wantedGain = (): number => (muted || hidden ? 0 : volume);

  /** Ramp one bus to a level over the short in-fade: a slider moved is never a pop. */
  function rampBus(bus: GainNode | null, target: number): void {
    if (ctx === null || bus === null) return;
    const t = ctx.currentTime;
    bus.gain.cancelScheduledValues(t);
    bus.gain.setValueAtTime(bus.gain.value, t);
    bus.gain.linearRampToValueAtTime(target, t + FADE.in * 0.35);
  }

  function applyGain(fadeSeconds: number): void {
    if (ctx === null || master === null) return;
    const target = wantedGain();
    const t = ctx.currentTime;
    master.gain.cancelScheduledValues(t);
    master.gain.setValueAtTime(master.gain.value, t);
    master.gain.linearRampToValueAtTime(target, t + Math.max(0.001, fadeSeconds));
    if (target === 0) {
      // Suspend AFTER the ramp, or the fade is a cut. The timer is the only one in this lane
      // and it exists for the length of one fade.
      const c = ctx;
      window.setTimeout(() => {
        if (c.state === 'running' && wantedGain() === 0) transition(c, 'suspended');
      }, Math.ceil(fadeSeconds * 1000) + 30);
    } else if (ctx.state === 'suspended') {
      transition(ctx, 'running');
    }
  }

  function buildMomentBuffers(context: BaseAudioContext, bank: MomentBank): void {
    momentBuffers = {
      land: bank.land.map((row) => row.map((b) => monoBuffer(context, b))),
      junk: bank.junk.map((b) => monoBuffer(context, b)),
      record: bank.record.map((b) => monoBuffer(context, b)),
      lift: monoBuffer(context, bank.lift),
      settle: monoBuffer(context, bank.settle),
      mark: bank.mark.map((b) => monoBuffer(context, b)),
    };
  }

  /** Put one layer on the graph, looping, and start it. */
  function startLayer(context: AudioContext, bus: GainNode, layer: SeaLayer): void {
    const src = context.createBufferSource();
    src.buffer = toAudioBuffer(context, layer);
    src.loop = true;
    src.connect(bus);
    // Each loop starts at a different point in its own buffer so the pair does not begin life
    // aligned at their common zero, which is the one instant the two are correlated.
    src.start(0, (layer.seconds * hashText(String(layer.length))) % layer.seconds);
    loops.push(src);
  }

  /**
   * THE RENDER, IN PIECES, AND WHY IT IS NOT ONE CALL.
   *
   * The first version rendered everything in the gesture handler and MEASURED 774 ms of blocked
   * main thread in Chrome - three quarters of a second of frozen page as the reward for
   * clicking. Two things fixed it and both are kept: the envelope stopped calling a sine per
   * sample (see filters.ts), and the remainder is split into five tasks queued one behind the
   * other, so the longest single piece of work owns the thread for about a tenth of a second.
   *
   * THE SEA STARTS AS SOON AS ITS OWN PIECES ARE DONE, before the moment bank exists. That is
   * free: the master gain fades in over 2.2 seconds anyway, so a bank that arrives a tenth of a
   * second into the fade arrives long before there is anything to play through it.
   */
  function bakeSteps(context: AudioContext, bus: GainNode): BakeStep[] {
    const sr = context.sampleRate;
    const surfaceLength = Math.round(SURFACE.seconds * sr);
    let envelopes: { low: Float32Array; high: Float32Array } | null = null;
    const sides: Float32Array[] = [];
    const steps: BakeStep[] = [
      {
        name: 'swell',
        run: () => {
          const layer = renderSwell(sr);
          seaShapes.push({ length: layer.length, channels: layer.channels.length, seconds: layer.seconds });
          startLayer(context, bus, layer);
        },
      },
      { name: 'surface envelopes', run: () => { envelopes = surfaceEnvelopes(surfaceLength); } },
    ];
    for (let side = 0; side < SURFACE_CHANNELS; side++) {
      steps.push({
        name: `surface channel ${side}`,
        run: () => {
          if (envelopes !== null) sides.push(renderSurfaceSide(sr, side, envelopes));
        },
      });
    }
    steps.push({
      name: 'surface level and start',
      run: () => {
        if (sides.length !== SURFACE_CHANNELS) return;
        levelSurfacePair(sides);
        const layer: SeaLayer = { channels: sides, length: surfaceLength, sampleRate: sr, seconds: surfaceLength / sr };
        seaShapes.push({ length: layer.length, channels: layer.channels.length, seconds: layer.seconds });
        startLayer(context, bus, layer);
        // The samples now live in the AudioBuffer. Dropping the source array here is the
        // difference between 9 MiB of audio and 17 MiB of audio for the identical sound.
        sides.length = 0;
      },
    });
    steps.push({
      name: 'moment bank',
      run: () => {
        bank = renderMomentBank(sr);
        buildMomentBuffers(context, bank);
      },
    });
    return steps;
  }

  function runBake(context: AudioContext, bus: GainNode): void {
    const steps = bakeSteps(context, bus);
    let i = 0;
    const next = (): void => {
      const step = steps[i++];
      if (step === undefined) return;
      const t0 = nowMs();
      step.run();
      const ms = nowMs() - t0;
      renderMs += ms;
      stepMs.push(+ms.toFixed(1));
      // Yield between pieces so the host page can draw. This is the only timer this lane sets
      // that is not the tail of a fade, and it stops after the last piece.
      window.setTimeout(next, 0);
    };
    next();
  }

  function start(): void {
    if (ctx !== null) return;
    try {
      ctx = new AudioContext();
    } catch {
      // No audio on this machine. Everything below is a no-op and the game is unchanged.
      ctx = null;
      return;
    }
    master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    seaGain = ctx.createGain();
    seaGain.gain.value = seaLevel;
    seaGain.connect(master);
    effectsGain = ctx.createGain();
    effectsGain.gain.value = effectsLevel;
    effectsGain.connect(master);
    runBake(ctx, seaGain);
    if (!muted && !hidden) {
      transition(ctx, 'running');
      applyGain(FADE.in);
    } else if (ctx.state === 'running') {
      transition(ctx, 'suspended');
    }
  }

  function onGesture(): void {
    const wasStarted = ctx !== null;
    start();
    if (!wasStarted && ctx !== null && onStarted !== null) {
      const cb = onStarted;
      onStarted = null;
      cb();
    }
  }

  function fire(buffer: AudioBuffer | undefined, detune: number, whenOffset = 0): void {
    if (ctx === null || effectsGain === null || buffer === undefined) return;
    if (muted || hidden) return;
    if (voices >= MAX_VOICES) return;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = detune;
    src.connect(effectsGain);
    voices++;
    momentsPlayed++;
    src.onended = (): void => {
      voices--;
      src.disconnect();
      src.onended = null;
    };
    src.start(ctx.currentTime + whenOffset);
  }

  /** A repeatable variant index and detune for one event token. */
  function variantOf(token: string, count: number): { index: number; detune: number } {
    const h = hashText(token);
    const index = count > 0 ? h % count : 0;
    // +/- 4%. Enough that two of the same fish differ, small enough that nothing changes pitch class.
    const detune = 0.96 + ((h >>> 8) % 81) / 1000;
    return { index, detune };
  }

  function play(event: AudioEvent): void {
    if (momentBuffers === null) return;
    switch (event.kind) {
      case 'land': {
        const t = nowMs();
        if (t - lastLandAtMs < LAND_MIN_GAP_MS) {
          landsSuppressed++;
          return;
        }
        lastLandAtMs = t;
        const bucket = Math.max(0, Math.min(LAND_BUCKETS - 1, Math.round(event.weightFraction * (LAND_BUCKETS - 1))));
        const row = momentBuffers.land[bucket];
        const v = variantOf(event.token, LAND_VARIANTS);
        fire(row?.[v.index], v.detune, LAND_AT);
        return;
      }
      case 'record': {
        const v = variantOf(event.token, momentBuffers.record.length);
        fire(momentBuffers.record[v.index], v.detune, RECORD_AT);
        return;
      }
      case 'special': {
        const v = variantOf(event.token, momentBuffers.record.length);
        fire(momentBuffers.record[v.index], v.detune * 1.06);
        return;
      }
      case 'junk': {
        const v = variantOf(event.token, momentBuffers.junk.length);
        fire(momentBuffers.junk[v.index], v.detune);
        return;
      }
      default:
        return;
    }
  }

  return {
    arm(started?: () => void): void {
      if (armed) return;
      armed = true;
      onStarted = started ?? null;
      if (typeof window === 'undefined') return;
      const once = { once: true, passive: true } as const;
      window.addEventListener('pointerdown', onGesture, once);
      window.addEventListener('keydown', onGesture, once);
      window.addEventListener('touchstart', onGesture, once);
      document.addEventListener('visibilitychange', () => {
        hidden = document.visibilityState === 'hidden';
        applyGain(hidden ? FADE.out : FADE.in * 0.4);
      });
    },
    get started(): boolean {
      return ctx !== null;
    },
    get muted(): boolean {
      return muted;
    },
    setMuted(next: boolean): boolean {
      muted = next;
      writeMuted(muted);
      // A player who mutes before ever clicking has still made a gesture, so the context can
      // exist; it is built suspended and costs nothing until they change their mind.
      if (ctx === null) start();
      applyGain(muted ? FADE.out : FADE.in * 0.35);
      return muted;
    },
    toggleMuted(): boolean {
      return this.setMuted(!muted);
    },
    setMix(mix: AudioMix): void {
      const wasMuted = muted;
      volume = level(mix.master, 1);
      seaLevel = level(mix.sea, 1);
      effectsLevel = level(mix.effects, 1);
      muted = mix.muted;
      if (muted !== wasMuted) writeMuted(muted);
      // Nothing to build for a level: a context that does not exist yet reads the three levels
      // when it comes up, and one that does ramps to them over the short in-fade.
      if (ctx === null) return;
      rampBus(seaGain, seaLevel);
      rampBus(effectsGain, effectsLevel);
      applyGain(muted && !wasMuted ? FADE.out : FADE.in * 0.35);
    },
    get volume(): number {
      return volume;
    },
    claimFirstTelling(): boolean {
      try {
        if (window.localStorage.getItem(TOLD_KEY) === '1') return false;
        window.localStorage.setItem(TOLD_KEY, '1');
        return true;
      } catch {
        // Storage refused. Tell them once this session rather than never.
        return true;
      }
    },
    observe(snapshot: AudioSnapshot): void {
      const next = snapshotOf(snapshot);
      if (last === null) {
        last = next;
        return;
      }
      const events = eventsBetween(last, next);
      last = next;
      if (ctx === null || muted || hidden) return;
      for (const e of events) play(e);
    },
    forget(): void {
      last = null;
      lastLandAtMs = -1e12;
    },
    sheet(up: boolean): void {
      if (momentBuffers === null) return;
      fire(up ? (momentBuffers.lift ?? undefined) : (momentBuffers.settle ?? undefined), 1);
    },
    mark(): void {
      if (momentBuffers === null) return;
      const v = variantOf(`mark:${momentsPlayed}`, momentBuffers.mark.length);
      fire(momentBuffers.mark[v.index], v.detune);
    },
    play,
    stats(): AudioStats {
      return {
        started: ctx !== null,
        muted,
        volume,
        sea: seaLevel,
        effects: effectsLevel,
        hidden,
        contextState: ctx === null ? null : ctx.state,
        sampleRate: ctx === null ? null : ctx.sampleRate,
        nodes: ctx === null ? 0 : 3 + loops.length + voices,
        renderMs: +renderMs.toFixed(2),
        renderStepMs: [...stepMs],
        bufferSamples: seaShapes.reduce((a, l) => a + l.length * l.channels, 0) + (bank?.samples ?? 0),
        loopSeconds: seaShapes.map((l) => +l.seconds.toFixed(3)),
        momentsPlayed,
        landsSuppressed,
      };
    },
  };
}
